import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "./Reveal";
import { LazyVideo } from "./LazyVideo";
import { VIDEOS, POSTERS, CONTACT } from "../i18n";

const heroVideos = [
  { src: VIDEOS.openHouse, poster: POSTERS.openHouse },
  { src: VIDEOS.boutique, poster: POSTERS.boutique },
  { src: VIDEOS.conference, poster: POSTERS.conference },
];

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, isDesktop ? -80 : 0]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, isDesktop ? -160 : 0]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, isDesktop ? -50 : 0]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, isDesktop ? 90 : 0]);
  const parallax = [y1, y2, y3];
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-screen pt-28 md:pt-36 pb-16 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-6 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-6 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.9 }}
            className="font-sans text-[11px] md:text-xs uppercase tracking-[0.3em] text-earth mb-8 md:mb-12"
          >
            {t.hero.overline}
          </motion.p>
          <h1 className="font-serif text-ink text-4xl sm:text-5xl lg:text-[3.8vw] leading-[1.12] tracking-tight" data-testid="hero-title">
            {t.hero.titleLines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.10em]">
                <motion.span
                  className={`block ${i > 0 ? "italic text-earth" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.3, ease: EASE, delay: 0.25 + i * 0.15 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 1.0 }}
            className="mt-10 md:mt-14 border-l border-earth/40 pl-6 space-y-1.5"
            data-testid="hero-signature"
          >
            <p className="font-sans text-xs uppercase tracking-[0.3em] text-ink">{t.hero.studio}</p>
            <p className="font-serif italic text-earth text-lg md:text-xl">{t.hero.tagline}</p>
            <p className="font-sans text-[11px] tracking-[0.2em] text-ink/50">{t.hero.by}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 1.25 }}
            className="flex flex-wrap items-center gap-5 mt-10 md:mt-14"
          >
            <button
              onClick={() => go("contact")}
              data-testid="hero-cta-start"
              className="font-sans text-xs uppercase tracking-[0.25em] bg-ink text-cream px-8 py-4 hover:bg-earth transition-colors duration-700"
            >
              {t.hero.cta1}
            </button>
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-whatsapp"
              className="font-sans text-xs uppercase tracking-[0.25em] text-ink border border-ink/25 px-8 py-[15px] hover:border-earth hover:text-earth transition-colors duration-700"
            >
              {t.hero.whatsapp}
            </a>
            <button
              onClick={() => go("work")}
              data-testid="hero-cta-work"
              className="font-sans text-xs uppercase tracking-[0.25em] text-ink border-b border-ink/30 pb-1 hover:border-earth hover:text-earth transition-colors duration-700"
            >
              {t.hero.cta2}
            </button>
          </motion.div>
        </motion.div>

        <div className="lg:col-span-6 relative">
          <div className="grid grid-cols-3 gap-3 md:gap-5 items-start">
            {heroVideos.map((v, i) => (
              <motion.div
                key={i}
                style={{ y: parallax[i] }}
                initial={{ opacity: 0, y: 60 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.4, ease: EASE, delay: 0.7 + i * 0.15 }}
                className={i === 1 ? "mt-12 md:mt-20" : i === 2 ? "mt-6 md:mt-10" : ""}
              >
                <div className="relative aspect-[9/16] overflow-hidden bg-stone" data-testid={`hero-video-${i}`}>
                  <LazyVideo src={v.src} poster={v.poster} className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <p className="font-sans text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-ink/40 mt-3">
                  {String(i + 1).padStart(2, "0")} — {t.hero.videoLabels[i]}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
